import type { MatchRuleSet, MatchRulesSettings, PhaseId, RoundKey } from '@/core/schemas';

/**
 * Resolves which rules apply to a match, by specificity:
 *
 *   round override  →  phase override  →  tournament default
 *
 * This function is the only place that answers "best of how many?". No number
 * is written down here; everything comes from the settings, which is what makes
 * "Gruppenphase Best of 3, Finale Best of 9" pure configuration.
 */
export function resolveMatchRules(
  settings: MatchRulesSettings,
  phaseId: PhaseId,
  roundKey: RoundKey,
): MatchRuleSet {
  return settings.byRound[roundKey] ?? settings.byPhase[phaseId] ?? settings.default;
}

/**
 * How many legs (or sets) a side must win to take the match.
 *
 * With an even `bestOf` both sides can reach this number only by playing every
 * unit, which is exactly how a draw becomes possible — intentional, because a
 * group phase may want draws.
 */
export function winsRequired(rules: MatchRuleSet): number {
  return Math.floor(rules.bestOf / 2) + 1;
}

/** The unit a match is decided in. */
export function scoringUnit(rules: MatchRuleSet): 'legs' | 'sets' {
  return rules.scoring;
}

export interface SideScore {
  readonly home: number;
  readonly away: number;
}

export type MatchOutcome =
  | { readonly kind: 'undecided' }
  | { readonly kind: 'draw' }
  | { readonly kind: 'decided'; readonly winner: 'home' | 'away' };

/**
 * Judges a score against the rules. Used to reject impossible reports — for
 * example 3:0 in a best of 3, where the match would already have ended at 2:0.
 */
export function judgeScore(score: SideScore, rules: MatchRuleSet): MatchOutcome {
  const needed = winsRequired(rules);
  const played = score.home + score.away;

  if (score.home >= needed && score.home > score.away) {
    return { kind: 'decided', winner: 'home' };
  }

  if (score.away >= needed && score.away > score.home) {
    return { kind: 'decided', winner: 'away' };
  }

  if (played >= rules.bestOf && score.home === score.away) {
    return { kind: 'draw' };
  }

  return { kind: 'undecided' };
}

/** Whether a reported score could actually have occurred under the rules. */
export function isPlausibleScore(score: SideScore, rules: MatchRuleSet): boolean {
  if (score.home < 0 || score.away < 0) {
    return false;
  }

  if (score.home + score.away > rules.bestOf) {
    return false;
  }

  const needed = winsRequired(rules);

  // Neither side can exceed what is needed to win — the match stops there.
  return score.home <= needed && score.away <= needed;
}
